import { usePublicMetadata } from '../../../seo/PublicMetadata.jsx';
import { useInitialPublicData } from '../../../seo/PublicBootstrap.jsx';
import { InlineFeedback } from '@mts/ui/feedback';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChartNoAxesColumnIncreasing, CheckCircle2, ChevronDown, House, Mail, Paintbrush, Search, Send, Sparkles, Tag } from 'lucide-react';
import { Badge, Button, Card, EmptyState, Input, Select } from '@mts/ui';
import { autocompleteSharedMailerBusinesses, getSharedMailerLandingPage, submitSharedMailerLead } from '../../../services/sharedMailers.js';
import { trackVisitorEvent } from '../../../services/visitorTracking.js';
import { usePublicTenant } from '../../../hooks/usePublicTenant.js';
import { buildPublicPageMetadata } from '../../../utils/publicMetadata.js';
import { buildBreadcrumbSchema, buildWebPageSchema } from '../../../utils/structuredData.js';

const SECTION_LINKS = [
  { id: 'overview', label: 'Overview' },
  { id: 'coverage', label: 'Local Reach' },
  { id: 'availability', label: 'Availability' },
  { id: 'pricing', label: 'Pricing' },
  { id: 'examples', label: 'Examples' },
  { id: 'faq', label: 'FAQ' },
  { id: 'reserve', label: 'Reserve' },
];

const STATUS_LABELS = {
  COMING_SOON: 'Coming soon',
  ACCEPTING_ADVERTISERS: 'Now accepting advertisers',
  ALMOST_FULL: 'Almost full',
  SOLD_OUT: 'Sold out',
  MAILED: 'Mailed',
  AVAILABLE: 'Available',
  PENDING: 'Pending',
  RESERVED: 'Reserved',
  SOLD: 'Sold',
  UNAVAILABLE: 'Not available',
};

const ACCENT_CTA_CLASS = 'bg-[var(--spotlight,#f59e0b)] text-[var(--spotlight-foreground,#111827)] hover:bg-[var(--spotlight-hover,var(--spotlight,#f59e0b))] hover:enabled:bg-[var(--spotlight-hover,var(--spotlight,#f59e0b))] focus:ring-[var(--spotlight,#f59e0b)]';
const RESERVE_CTA_LABEL = 'Reserve My Spot';

function formatNumber(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed.toLocaleString() : '';
}

function formatCurrency(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }) : '';
}

function scrollToSection(id) {
  const element = document.getElementById(id);
  if (!element) return;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
}

function MailerSectionNav({ primaryLabel }) {
  return (
    <div className="sticky top-0 z-30 border-b border-[var(--border,#e2e8f0)] bg-[var(--surface,#fff)]/95 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center gap-3 px-4 py-2 sm:px-6">
        <div className="flex min-w-0 flex-1 gap-1 overflow-x-auto">
          {SECTION_LINKS.map((link) => (
            <button
              key={link.id}
              type="button"
              onClick={() => scrollToSection(link.id)}
              className="whitespace-nowrap rounded-md px-3 py-2 text-sm font-semibold text-[var(--muted,#64748b)] outline-none transition hover:bg-[var(--primary-soft,#dbeafe)] hover:text-[var(--primary,#1d4ed8)] focus-visible:ring-2 focus-visible:ring-[var(--primary,#1d4ed8)]"
            >
              {link.label}
            </button>
          ))}
        </div>
        <Button size="sm" className={ACCENT_CTA_CLASS} onClick={() => scrollToSection('reserve')}>
          {primaryLabel || RESERVE_CTA_LABEL}
        </Button>
      </div>
    </div>
  );
}

function MailerMockup({ tenant, campaign }) {
  const sampleImage = campaign?.examples?.[0]?.frontImageUrl || '';

  if (sampleImage) {
    return (
      <div className="relative mx-auto w-full max-w-[49rem] overflow-hidden rounded-[8px] border border-slate-200 bg-white shadow-2xl">
        <img
          src={sampleImage}
          alt={campaign?.examples?.[0]?.altText || 'Shared mailer sample'}
          className="aspect-[16/10] w-full object-contain"
          loading="eager"
        />
      </div>
    );
  }

  return (
    <div className="relative mx-auto aspect-[16/10] w-full max-w-[49rem] overflow-hidden rounded-[8px] border border-slate-200 bg-white p-4 shadow-2xl">
      <div className="flex h-full flex-col rounded-[6px] border border-slate-200 bg-slate-50">
        <div className="flex items-center justify-between border-b border-slate-200 bg-[var(--primary,#1d4ed8)] px-4 py-3 text-[var(--primary-foreground,#fff)]">
          <div className="min-w-0">
            <p className="truncate text-xs font-semibold uppercase tracking-[0.16em]">Shared Mailer</p>
            <p className="truncate text-lg font-bold">{tenant?.marketName || tenant?.name}</p>
          </div>
          {tenant?.logoUrl ? <img src={tenant.logoUrl} alt="" className="h-10 w-10 rounded bg-white object-contain p-1" /> : <Mail size={32} />}
        </div>
        <div className="grid flex-1 grid-cols-2 gap-2 p-3">
          {['Standard', 'Featured', 'Premium', 'Local Offer', 'QR Code', 'Directory'].map((label, index) => (
            <div key={label} className={`rounded-md border border-slate-200 bg-white p-3 ${index === 1 ? 'col-span-2' : ''}`}>
              <div className="mb-3 h-12 rounded bg-[var(--primary-soft,#dbeafe)]" />
              <p className="text-xs font-bold text-slate-900">{label}</p>
              <p className="mt-1 text-[11px] leading-4 text-slate-500">Local business space</p>
            </div>
          ))}
        </div>
        <div className="border-t border-slate-200 p-3 text-center text-xs font-semibold text-slate-600">
          {campaign?.name || 'Upcoming community mailer'}
        </div>
      </div>
    </div>
  );
}

function CountUpNumber({ value }) {
  const numeric = Number(value);
  return <span>{value != null && Number.isFinite(numeric) ? numeric.toLocaleString('en-US') : 'Not configured'}</span>;
}

function MailerHero({ data }) {
  const campaign = data.campaign;
  const householdCount = Number(data.hero?.householdCount);
  const placeName = data.hero?.placeName || 'Local';

  return (
    <section id="overview" className="scroll-mt-24 bg-[var(--background,#f8fafc)]">
      <div className="mx-auto grid max-w-7xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[minmax(0,1fr)_588px] lg:items-center lg:py-20">
        <div>
          {campaign && data.hero.eyebrow ? (
            <p className="text-sm font-bold uppercase tracking-[0.18em] text-[var(--primary,#1d4ed8)]">{data.hero.eyebrow}</p>
          ) : null}
          <h1 className={`${campaign && data.hero.eyebrow ? 'mt-4' : ''} max-w-4xl text-4xl font-bold leading-tight text-[var(--text,#0f172a)] sm:text-5xl lg:text-6xl`}>
            Reach {householdCount > 0 ? <><CountUpNumber value={householdCount} /> mailboxes</> : 'local customers'} in {placeName} With One Simple Campaign
          </h1>
          <p className="mt-5 max-w-2xl text-lg leading-8 text-[var(--muted,#64748b)]">
            {campaign ? data.hero.description : data.emptyState?.description}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button size="lg" className={ACCENT_CTA_CLASS} onClick={() => scrollToSection('reserve')}>
              {RESERVE_CTA_LABEL}
              <ArrowRight size={18} />
            </Button>
            <Button size="lg" variant="outline" onClick={() => scrollToSection('coverage')}>
              {data.hero.secondaryCtaLabel}
            </Button>
          </div>
          {campaign?.availableSpotCount ? <div className="mt-6 text-sm text-[var(--muted,#64748b)]">{campaign.availableSpotCount} spaces currently available</div> : null}
        </div>
        <MailerMockup tenant={data.tenant} campaign={campaign || { examples: data.examples || [] }} />
      </div>
    </section>
  );
}

function MailerHowItWorks({ tenant }) {
  const tenantName = tenant?.name || tenant?.marketName || 'Tenant Spotlight';
  const tenantCity = tenant?.city || tenant?.marketName || 'local';
  const steps = [
    {
      title: 'Choose Your Space',
      description: 'Select the ad size that fits your campaign.',
      benefit: 'Multiple ad sizes to fit your campaign',
      benefitIcon: Tag,
      imageAlt: 'Ad size and shared mailer layout options',
    },
    {
      title: 'We Create Your Ad',
      description: "We'll turn your offer and branding into a professionally designed advertisement.",
      benefit: 'Professional ad design included',
      benefitIcon: Paintbrush,
      imageUrl: '/assets/mailers/mailer-ad-design-monitor.png',
      imageAlt: 'Professional advertisement being designed on a desktop monitor',
    },
    {
      title: 'Reach Local Households',
      description: `Your business appears on a large shared mailer delivered throughout the selected ${tenantCity} coverage area.`,
      benefit: 'Delivered directly to local households',
      benefitIcon: House,
      imageUrl: '/assets/mailers/shared-mailer-mailbox-delivery.png',
      imageAlt: 'Large shared mailer being delivered from an open residential mailbox',
    },
    {
      title: 'Track Engagement',
      description: 'Use dedicated offers, QR destinations, and campaign-specific links to better understand customer response.',
      benefit: 'Practical ways to understand engagement',
      benefitIcon: ChartNoAxesColumnIncreasing,
      imageUrl: '/assets/mailers/mailer-campaign-qr-analytics.png',
      imageAlt: 'Mobile QR code offer beside a mailer campaign analytics dashboard',
      learnMoreHref: '/advertise/mailer/tracking',
    },
  ];
  const reasons = [
    `Delivered directly to households in the ${tenantCity} community`,
    'Affordable shared-cost advertising with strong local reach',
    'Professionally designed ads that help your business stand out',
    'Promotions and coupons that give residents a reason to act',
    'QR codes that turn mailbox exposure into website visits, calls, and leads',
    'Limited local business participation to keep the mailer useful and uncluttered',
  ];

  return (
    <section id="how-it-works" className="scroll-mt-24 bg-white py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">Simple. Effective. Local.</p>
          <h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">How It Works</h2>
          <p className="mt-3 text-base leading-7 text-[var(--muted,#64748b)]">We make local advertising easy and effective. You focus on your business, we handle the rest.</p>
        </div>
        <div className="mt-8 grid items-stretch gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {steps.map((step, index) => (
            <Card key={step.title} className="h-full border border-[var(--border,#e2e8f0)] bg-white shadow-none">
              <Card.Body className="flex h-full flex-col p-4">
                <div className="flex min-h-9 items-center gap-3">
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--primary,#1d4ed8)] text-sm font-bold text-white">{index + 1}</div>
                  <h3 className="text-lg font-bold leading-6 text-[var(--text,#0f172a)]">{step.title}</h3>
                </div>
                <p className="mt-3 min-h-16 text-sm leading-5 text-[var(--muted,#64748b)]">{step.description}</p>
                {step.learnMoreHref ? <Link to={step.learnMoreHref} className="mt-2 inline-flex w-fit items-center gap-1 text-sm font-bold text-[var(--primary,#1d4ed8)] underline-offset-4 hover:underline focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary,#1d4ed8)]">Learn how tracking works<ArrowRight size={15} aria-hidden="true" /></Link> : null}
                <div className="mt-4 flex aspect-[4/3] w-full items-center justify-center overflow-hidden rounded-md bg-[var(--primary-soft,#dbeafe)] p-1.5">
                  {step.imageUrl ? <img
                    src={step.imageUrl}
                    alt={step.imageAlt}
                    className="h-full w-full object-contain"
                    loading="lazy"
                  /> : <div role="img" aria-label={step.imageAlt} className="grid h-[88%] w-[88%] grid-cols-3 grid-rows-3 gap-2 rounded-md border border-dashed border-[var(--primary,#1d4ed8)] bg-white p-3">
                    <span className="row-span-2 rounded-sm bg-[var(--primary,#1d4ed8)]" />
                    <span className="col-span-2 rounded-sm bg-[var(--primary-soft,#dbeafe)]" />
                    <span className="rounded-sm bg-[var(--primary-soft,#dbeafe)]" />
                    <span className="rounded-sm bg-[var(--primary-soft,#dbeafe)]" />
                    <span className="rounded-sm bg-[var(--primary-soft,#dbeafe)]" />
                    <span className="rounded-sm bg-[var(--primary-soft,#dbeafe)]" />
                    <span className="rounded-sm bg-[var(--primary-soft,#dbeafe)]" />
                  </div>}
                </div>
                <div className="mt-4 flex min-h-12 items-center gap-3">
                  <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[var(--primary-soft,#dbeafe)] text-[var(--primary,#1d4ed8)]">
                    {React.createElement(step.benefitIcon, { size: 20, strokeWidth: 2, 'aria-hidden': true })}
                  </span>
                  <p className="text-xs font-medium leading-5 text-[var(--text,#0f172a)]">{step.benefit}</p>
                </div>
              </Card.Body>
            </Card>
          ))}
        </div>
        <div className="mt-16 grid gap-8 sm:mt-20 lg:grid-cols-[minmax(0,1fr)_420px] lg:items-center">
          <div>
            <div className="space-y-5 text-base leading-8 text-[var(--muted,#64748b)]">
              <p>
                Big advertising platforms can be expensive and easy to get lost in. {tenantName} puts your business directly in front of local households in the community you serve.
              </p>
              <p>
                Our shared mailer brings together a limited group of local businesses, giving residents useful offers while giving your business strong local exposure for a fraction of the cost of mailing alone.
              </p>
            </div>
            <h3 className="mt-8 text-xl font-bold text-[var(--text,#0f172a)]">Why Local Businesses Choose {tenantName}</h3>
            <ul className="mt-5 grid gap-3 text-sm leading-6 text-[var(--muted,#64748b)] sm:grid-cols-2">
              {reasons.map((reason) => (
                <li key={reason} className="flex gap-2">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-[var(--primary,#1d4ed8)]" />
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
          <img
            src="/assets/mailers/shared-mailer-household.png"
            alt="A family reading a local shared mailer at home"
            className="mx-auto aspect-[4/5] w-full max-w-md rounded-[8px] object-cover shadow-xl"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

function geometryPolygons(geometry) {
  if (!geometry) return [];
  if (geometry.type === 'Feature') return geometryPolygons(geometry.geometry);
  if (geometry.type === 'FeatureCollection') return (geometry.features || []).flatMap((feature) => geometryPolygons(feature));
  if (geometry.type === 'Polygon') return [geometry.coordinates || []];
  if (geometry.type === 'MultiPolygon') return geometry.coordinates || [];
  return [];
}

function geometryBounds(geometries) {
  const points = [];
  geometries.forEach((geometry) => {
    geometryPolygons(geometry).forEach((polygon) => {
      polygon.forEach((ring) => {
        ring.forEach((point) => {
          const lng = Number(point?.[0]);
          const lat = Number(point?.[1]);
          if (Number.isFinite(lng) && Number.isFinite(lat)) points.push([lng, lat]);
        });
      });
    });
  });
  if (!points.length) return null;
  return points.reduce((bounds, [lng, lat]) => ({
    minLng: Math.min(bounds.minLng, lng),
    minLat: Math.min(bounds.minLat, lat),
    maxLng: Math.max(bounds.maxLng, lng),
    maxLat: Math.max(bounds.maxLat, lat),
  }), {
    minLng: Infinity,
    minLat: Infinity,
    maxLng: -Infinity,
    maxLat: -Infinity,
  });
}

function coverageMapPath(geometry, projectPoint) {
  return geometryPolygons(geometry).map((polygon) => (
    polygon.map((ring) => ring.map((point, index) => {
      const projected = projectPoint(point);
      return `${index === 0 ? 'M' : 'L'} ${projected.x.toFixed(2)} ${projected.y.toFixed(2)}`;
    }).join(' ')).join(' Z ') + ' Z'
  )).join(' ');
}

function StaticCoverageMap({ coverageMap }) {
  const placeGeometry = coverageMap?.place?.geomGeojson;
  const zones = Array.isArray(coverageMap?.zones) ? coverageMap.zones.filter((zone) => zone?.geomGeojson) : [];
  const bounds = geometryBounds([placeGeometry, ...zones.map((zone) => zone.geomGeojson)]);

  if (!bounds) {
    return (
      <div className="flex aspect-[16/10] items-center justify-center rounded-[8px] bg-slate-100 text-sm font-semibold text-[var(--muted,#64748b)]">
        Coverage map unavailable
      </div>
    );
  }

  const width = 800;
  const height = 500;
  const padding = 34;
  const lngSpan = Math.max(bounds.maxLng - bounds.minLng, 0.000001);
  const latSpan = Math.max(bounds.maxLat - bounds.minLat, 0.000001);
  const scale = Math.min((width - padding * 2) / lngSpan, (height - padding * 2) / latSpan);
  const mapWidth = lngSpan * scale;
  const mapHeight = latSpan * scale;
  const offsetX = (width - mapWidth) / 2;
  const offsetY = (height - mapHeight) / 2;
  const projectPoint = ([lng, lat]) => ({
    x: offsetX + (Number(lng) - bounds.minLng) * scale,
    y: offsetY + (bounds.maxLat - Number(lat)) * scale,
  });

  return (
    <svg
      role="img"
      aria-label="Mailer coverage map"
      viewBox={`0 0 ${width} ${height}`}
      className="aspect-[16/10] w-full rounded-[8px] bg-[#edf4f7]"
    >
      <rect width={width} height={height} fill="#edf4f7" />
      <path d="M0 72H800M0 144H800M0 216H800M0 288H800M0 360H800M0 432H800M100 0V500M200 0V500M300 0V500M400 0V500M500 0V500M600 0V500M700 0V500" stroke="#d8e4ea" strokeWidth="1" opacity="0.55" />
      {placeGeometry ? (
        <path
          d={coverageMapPath(placeGeometry, projectPoint)}
          fill="#ffffff"
          fillOpacity="0.7"
          stroke="#0f172a"
          strokeOpacity="0.55"
          strokeWidth="2.5"
          fillRule="evenodd"
        />
      ) : null}
      {zones.map((zone, index) => {
        const color = zone.color || ['#2563eb', '#0f766e', '#d97706', '#7c3aed', '#dc2626'][index % 5];
        return (
          <path
            key={zone.id || index}
            d={coverageMapPath(zone.geomGeojson, projectPoint)}
            fill={color}
            fillOpacity="0.28"
            stroke={color}
            strokeOpacity="0.88"
            strokeWidth="2"
            fillRule="evenodd"
          />
        );
      })}
      {placeGeometry ? (
        <path
          d={coverageMapPath(placeGeometry, projectPoint)}
          fill="none"
          stroke="#111827"
          strokeWidth="3.5"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      ) : null}
    </svg>
  );
}

function MailerCoverageSection({ campaign, coverageMap, tenant }) {
  const zone = campaign?.zone;
  const spotlightName = tenant?.name || tenant?.marketName || 'Local Spotlight';
  const deliveryCount = Number(zone?.deliveryCount || campaign?.deliveryCount || 0);
  const hasCategoryInventory = Number(campaign?.totalSpotCount || 0) > 0 || (campaign?.categories || []).length > 0;
  const hasNineByTwelveFormat = /(?:9\s*[×x]\s*12|12\s*[×x]\s*9)/i.test(String(campaign?.name || ''))
    || (campaign?.adProducts || []).some((product) => {
    const dimensions = String(product?.dimensions || product?.sizeLabel || '').replace(/[^0-9x]/gi, '').toLowerCase();
    return dimensions.includes('9x12') || dimensions.includes('12x9');
    });
  const hasFlatPrice = (campaign?.adProducts || []).some((product) => Number(product?.price) > 0);
  const coverageCards = [
    deliveryCount > 0 ? {
      headline: formatNumber(deliveryCount),
      label: 'Local Mailboxes',
      copy: 'Put your business in front of thousands of nearby households.',
    } : null,
    hasCategoryInventory ? {
      headline: 'Category Exclusive',
      label: '1 Per Category',
      copy: 'Your direct competitors stay off the same mailer.',
    } : null,
    hasNineByTwelveFormat ? {
      headline: '9 × 12',
      label: 'Hard to Miss',
      copy: 'An oversized format built to stand out in the mailbox.',
    } : null,
    campaign ? {
      headline: 'Front & Center',
      label: 'No Envelope to Open',
      copy: 'Your message is visible the moment the mailer is picked up.',
    } : null,
    zone ? {
      headline: 'Local Only',
      label: 'Reach Your Backyard',
      copy: 'Stay visible to the households most likely to become local customers.',
    } : null,
    hasFlatPrice ? {
      headline: 'One Flat Price',
      label: 'Shared Cost. Big Reach.',
      copy: 'Get large-scale local exposure without paying for a solo mailing.',
    } : null,
  ].filter(Boolean).slice(0, 4);
  const demographics = zone?.demographicSummary && typeof zone.demographicSummary === 'object'
    ? Object.entries(zone.demographicSummary).filter(([, value]) => value !== null && value !== undefined && value !== '')
    : [];

  return (
    <section id="coverage" className="scroll-mt-24 bg-[var(--background,#f8fafc)] py-14">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[1fr_1.2fr]">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">Local Reach</p>
          <h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">{zone?.name || 'Local campaign coverage'}</h2>
          <p className="mt-4 text-base leading-7 text-[var(--muted,#64748b)]">
            {zone?.publicDescription || `Here's who will see your ad in the ${spotlightName} Monthly Mailer`}
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2">
            {coverageCards.map((card) => (
              <div key={card.label} className="rounded-[8px] border border-[var(--border,#e2e8f0)] bg-white p-4">
                <p className="text-xl font-extrabold uppercase leading-tight text-[var(--text,#0f172a)]">{card.headline}</p>
                <p className="mt-1 text-xs font-bold uppercase tracking-[0.12em] text-[var(--primary,#1d4ed8)]">{card.label}</p>
                <p className="mt-3 text-sm leading-5 text-[var(--muted,#64748b)]">{card.copy}</p>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-[8px] border border-[var(--border,#e2e8f0)] bg-white p-4 shadow-sm">
          <StaticCoverageMap coverageMap={coverageMap} />
          {demographics.length ? (
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {demographics.map(([key, value]) => <Metric key={key} label={key.replace(/([A-Z])/g, ' $1')} value={String(value)} />)}
            </div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function Metric({ icon: Icon, label, value }) {
  return (
    <div className="rounded-[8px] border border-[var(--border,#e2e8f0)] bg-white p-4">
      {Icon ? <Icon size={18} className="mb-2 text-[var(--primary,#1d4ed8)]" /> : null}
      <p className="text-lg font-bold capitalize text-[var(--text,#0f172a)]">{value}</p>
      <p className="text-xs font-semibold uppercase tracking-[0.12em] text-[var(--muted,#64748b)]">{label}</p>
    </div>
  );
}

function MailerCategoryAvailability({ categories, onSelect, showAll = false }) {
  const [group, setGroup] = useState('all');
  const groups = useMemo(() => ['all', ...Array.from(new Set(categories.map((category) => category.group || 'Other')))], [categories]);
  const visible = group === 'all' ? categories : categories.filter((category) => (category.group || 'Other') === group);
  const displayedCategories = showAll ? visible : visible.slice(0, 6);

  if (!categories.length) return null;

  return (
    <section id="availability" className="scroll-mt-24 bg-white py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">Live Availability</p>
            <h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">Reserve an exclusive category</h2>
          </div>
          {groups.length > 2 ? (
            <Select value={group} onChange={(event) => setGroup(event.target.value)}>
              {groups.map((item) => <option key={item} value={item}>{item === 'all' ? 'All categories' : item}</option>)}
            </Select>
          ) : null}
        </div>
        <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {displayedCategories.map((category) => {
            const available = category.status === 'AVAILABLE';
            return (
              <Card key={category.id} className="border border-[var(--border,#e2e8f0)]">
                <Card.Body>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-bold text-[var(--text,#0f172a)]">{category.name}</p>
                      <p className="mt-1 text-sm text-[var(--muted,#64748b)]">{category.group || 'Other'}</p>
                    </div>
                    <Badge variant={available ? 'success' : category.status === 'PENDING' ? 'warning' : 'secondary'}>
                      {STATUS_LABELS[category.status] || category.status}
                    </Badge>
                  </div>
                  {!available ? <p className="mt-4 text-sm text-[var(--muted,#64748b)]">{category.publicAdvertiserName || 'Reserved by a local business'}</p> : null}
                  {available ? (
                    <Button className="mt-5 w-full" variant="outline" onClick={() => onSelect(category.id)}>
                      {category.ctaLabel || 'Reserve'}
                    </Button>
                  ) : null}
                </Card.Body>
              </Card>
            );
          })}
        </div>
        {!showAll && categories.length > 6 ? (
          <div className="mt-8 text-center">
            <Link
              to="/mailer/availability"
              className="inline-flex h-11 items-center justify-center rounded-md border border-[var(--primary,#1d4ed8)] bg-white px-6 text-sm font-bold text-[var(--primary,#1d4ed8)] transition hover:bg-[var(--primary-soft,#dbeafe)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary,#1d4ed8)]"
            >
              View all
            </Link>
          </div>
        ) : null}
      </div>
    </section>
  );
}

function MailerPricingSection({ campaign, showPricing, onSelect }) {
  const products = campaign?.adProducts || [];
  if (!products.length) return null;

  return (
    <section id="pricing" className="scroll-mt-24 bg-[var(--background,#f8fafc)] py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">Pricing</p>
        <h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">Ad sizes and campaign options</h2>
        <div className="mt-8 grid gap-4 lg:grid-cols-3">
          {products.map((product) => (
            <Card key={product.id} className="border border-[var(--border,#e2e8f0)]">
              <Card.Body>
                <p className="text-xl font-bold text-[var(--text,#0f172a)]">{product.name}</p>
                <p className="mt-1 text-sm text-[var(--muted,#64748b)]">{product.sizeLabel || product.dimensions}</p>
                <p className="mt-5 text-3xl font-bold text-[var(--text,#0f172a)]">{showPricing && product.price ? formatCurrency(product.price) : 'Request Pricing'}</p>
                {product.description ? <p className="mt-3 text-sm leading-6 text-[var(--muted,#64748b)]">{product.description}</p> : null}
                <ul className="mt-5 space-y-2 text-sm text-[var(--muted,#64748b)]">
                  {product.includedDesign ? <li className="flex gap-2"><CheckCircle2 size={16} className="mt-0.5 text-[var(--primary,#1d4ed8)]" />Ad design included</li> : null}
                  {product.revisions ? <li className="flex gap-2"><CheckCircle2 size={16} className="mt-0.5 text-[var(--primary,#1d4ed8)]" />{product.revisions} revision{product.revisions === 1 ? '' : 's'}</li> : null}
                  {(product.includes || []).slice(0, 5).map((item) => <li key={item} className="flex gap-2"><CheckCircle2 size={16} className="mt-0.5 text-[var(--primary,#1d4ed8)]" />{item}</li>)}
                </ul>
                <Button className={`mt-6 w-full ${ACCENT_CTA_CLASS}`} onClick={() => onSelect(product.id)}>
                  Reserve {product.name}
                </Button>
              </Card.Body>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function MailerExamplesGallery({ examples }) {
  if (!examples.length) return null;
  return (
    <section id="examples" className="scroll-mt-24 bg-white py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">Examples</p>
        <h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">Previous mailer examples</h2>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {examples.map((example) => (
            <Card key={example.id} className="overflow-hidden border border-[var(--border,#e2e8f0)]">
              <div className="grid grid-cols-2 gap-1 bg-slate-100 p-1">
                {example.frontImageUrl ? (
                  <img src={example.frontImageUrl} alt={`${example.altText} front`} className="aspect-[9/12] w-full rounded-[6px] object-cover" loading="lazy" />
                ) : null}
                {example.backImageUrl ? (
                  <img src={example.backImageUrl} alt={`${example.altText} back`} className="aspect-[9/12] w-full rounded-[6px] object-cover" loading="lazy" />
                ) : null}
              </div>
              <Card.Body>
                <p className="font-bold text-[var(--text,#0f172a)]">{example.campaignName}</p>
                <p className="mt-1 text-sm text-[var(--muted,#64748b)]">{example.market || 'Sample shared mailer format'}</p>
              </Card.Body>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

function MailerPlatformBenefits({ data }) {
  const benefits = data.config.benefits || [];
  if (!benefits.length) return null;

  return (
    <section className="bg-[var(--background,#f8fafc)] py-14">
      <div className="mx-auto max-w-7xl px-4 sm:px-6">
        <div className="rounded-[8px] bg-[var(--primary,#1d4ed8)] p-8 text-[var(--primary-foreground,#fff)]">
          <Sparkles size={28} />
          <h2 className="mt-4 text-3xl font-bold">Your Mailer Campaign Doesn&apos;t End at the Mailbox</h2>
          <p className="mt-3 max-w-3xl text-base leading-7 opacity-90">
            Every participating advertiser can connect their print campaign to {data.tenant.name}, giving residents another way to discover your business, redeem your offer and engage online.
          </p>
          <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {benefits.map((benefit) => <div key={benefit} className="rounded-[8px] bg-white/12 px-4 py-3 text-sm font-semibold">{benefit}</div>)}
          </div>
        </div>
      </div>
    </section>
  );
}

function MailerFaq({ faq }) {
  if (!faq.length) return null;
  return (
    <section id="faq" className="scroll-mt-24 bg-white py-14">
      <div className="mx-auto max-w-4xl px-4 sm:px-6">
        <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">FAQ</p>
        <h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">Common questions</h2>
        <div className="mt-8 divide-y divide-[var(--border,#e2e8f0)] rounded-[8px] border border-[var(--border,#e2e8f0)]">
          {faq.map((item) => (
            <details key={item.question} className="group p-5" onToggle={(event) => {
              if (event.currentTarget.open) trackVisitorEvent({ eventType: 'mailer_faq_opened', metadata: { question: item.question } });
            }}>
              <summary className="cursor-pointer list-none font-bold text-[var(--text,#0f172a)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary,#1d4ed8)]">{item.question}</summary>
              <p className="mt-3 text-sm leading-6 text-[var(--muted,#64748b)]">{item.answer}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
}

function resolveMailerSubcategoryId(categoryGroups, categoryId) {
  if (!categoryId) return '';
  const selectedChild = categoryGroups.flatMap((parent) => parent.children).find((child) => child.id === categoryId || child.slug === categoryId);
  if (selectedChild) return selectedChild.id;
  return categoryGroups.find((parent) => parent.id === categoryId || parent.slug === categoryId)?.children?.[0]?.id || '';
}

function GroupedCategoryCombobox({ groups, value, onChange }) {
  const selected = groups.flatMap((parent) => parent.children).find((child) => child.id === value) || null;
  const [query, setQuery] = useState(selected?.name || '');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setQuery(selected?.name || '');
  }, [selected?.id, selected?.name]);

  const normalizedQuery = query.trim().toLowerCase();
  const matchingGroups = groups.map((parent) => ({
    ...parent,
    children: parent.children.filter((child) => !normalizedQuery
      || child.name.toLowerCase().includes(normalizedQuery)
      || parent.name.toLowerCase().includes(normalizedQuery)),
  })).filter((parent) => parent.children.length > 0);

  return <label className="grid gap-1.5 text-sm font-semibold text-[var(--text,#0f172a)]">
    Business category
    <div className="relative">
      <Search size={16} className="pointer-events-none absolute left-3 top-3.5 text-[var(--muted,#64748b)]" />
      <input
        type="search"
        role="combobox"
        aria-expanded={open}
        aria-controls="mailer-category-options"
        autoComplete="off"
        value={query}
        onFocus={() => setOpen(true)}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onChange={(event) => { setQuery(event.target.value); onChange(''); setOpen(true); }}
        placeholder="Search subcategories"
        required
        className="h-11 w-full rounded-md border border-[var(--border,#cbd5e1)] bg-white pl-9 pr-9 text-sm font-normal outline-none transition focus:border-[var(--primary,#1d4ed8)] focus:ring-2 focus:ring-[var(--primary-soft,#dbeafe)]"
      />
      <ChevronDown size={16} className="pointer-events-none absolute right-3 top-3.5 text-[var(--muted,#64748b)]" />
      {open ? <div id="mailer-category-options" className="absolute z-40 mt-1 max-h-72 w-full overflow-y-auto rounded-md border border-[var(--border,#cbd5e1)] bg-white py-1 shadow-xl">
        {matchingGroups.map((parent) => <div key={parent.id}>
          <div className="sticky top-0 bg-slate-50 px-3 py-2 text-xs font-bold uppercase tracking-wide text-slate-500">{parent.name}</div>
          {parent.children.map((child) => <button
            key={child.id}
            type="button"
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => { onChange(child.id); setQuery(child.name); setOpen(false); }}
            className={`block w-full px-3 py-2 text-left text-sm font-normal hover:bg-[var(--primary-soft,#dbeafe)] ${child.id === value ? 'bg-[var(--primary-soft,#dbeafe)] font-semibold text-[var(--primary,#1d4ed8)]' : 'text-[var(--text,#0f172a)]'}`}
          >{child.name}</button>)}
        </div>)}
        {!matchingGroups.length ? <p className="px-3 py-4 text-sm font-normal text-slate-500">No matching subcategories.</p> : null}
      </div> : null}
    </div>
  </label>;
}

function createBusinessSessionToken() {
  return globalThis.crypto?.randomUUID?.() || `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 18)}`;
}

function BusinessAutocomplete({ value, selection, onChange, onSelect }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [googleUnavailable, setGoogleUnavailable] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const requestRef = useRef(0);
  const sessionRef = useRef(createBusinessSessionToken());
  const manualIndex = results.length;

  useEffect(() => {
    const query = value.trim();
    if (selection || query.length < 2) { setResults([]); setLoading(false); return undefined; }
    const requestId = ++requestRef.current;
    setLoading(true); setOpen(true);
    const timer = window.setTimeout(async () => {
      try {
        const payload = await autocompleteSharedMailerBusinesses(query, sessionRef.current);
        if (requestRef.current !== requestId) return;
        setResults((payload?.results || []).slice(0, 3));
        setGoogleUnavailable(payload?.googleUnavailable === true);
      } catch { if (requestRef.current === requestId) { setResults([]); setGoogleUnavailable(true); } }
      finally { if (requestRef.current === requestId) setLoading(false); }
    }, 250);
    return () => window.clearTimeout(timer);
  }, [selection, value]);

  const choose = (result) => {
    onSelect({ ...result, sessionToken: sessionRef.current });
    setOpen(false); setActiveIndex(-1);
    if (result.source === 'GOOGLE') sessionRef.current = createBusinessSessionToken();
  };
  const chooseManual = () => { onSelect({ source: 'MANUAL', name: value, sessionToken: null }); setOpen(false); setActiveIndex(-1); };
  const handleKeyDown = (event) => {
    if (!open || value.trim().length < 2) return;
    if (event.key === 'ArrowDown') { event.preventDefault(); setActiveIndex((index) => Math.min(manualIndex, index + 1)); }
    if (event.key === 'ArrowUp') { event.preventDefault(); setActiveIndex((index) => Math.max(0, index - 1)); }
    if (event.key === 'Escape') setOpen(false);
    if (event.key === 'Enter' && activeIndex >= 0) { event.preventDefault(); activeIndex === manualIndex ? chooseManual() : choose(results[activeIndex]); }
  };

  return <label className="grid gap-1.5 text-sm font-semibold text-[var(--text,#0f172a)]">
    Business
    <div className="relative">
      <input role="combobox" aria-expanded={open && value.trim().length >= 2} aria-controls="mailer-business-options" aria-activedescendant={activeIndex >= 0 ? `mailer-business-option-${activeIndex}` : undefined}
        autoComplete="off" required value={value} onKeyDown={handleKeyDown} onFocus={() => { if (!selection && value.trim().length >= 2) setOpen(true); }}
        onBlur={() => window.setTimeout(() => setOpen(false), 150)} onChange={(event) => { onChange(event.target.value); setOpen(true); setActiveIndex(-1); }}
        placeholder="Search for your business" className="h-11 w-full rounded-md border border-[var(--border,#cbd5e1)] bg-white px-3 text-sm font-normal outline-none transition focus:border-[var(--primary,#1d4ed8)] focus:ring-2 focus:ring-[var(--primary-soft,#dbeafe)]" />
      {selection && selection.source !== 'MANUAL' ? <button type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => { onSelect(null); setOpen(true); }} className="absolute right-2 top-2 rounded px-2 py-1 text-xs text-[var(--primary,#1d4ed8)] hover:bg-[var(--primary-soft,#dbeafe)]">Change</button> : null}
      {open && value.trim().length >= 2 && !selection ? <div id="mailer-business-options" role="listbox" className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-[var(--border,#cbd5e1)] bg-white shadow-xl">
        {results.map((result, index) => <button id={`mailer-business-option-${index}`} role="option" aria-selected={activeIndex === index} key={`${result.source}:${result.listingId || result.googlePlaceId}`} type="button" onMouseDown={(event) => event.preventDefault()} onClick={() => choose(result)} className={`flex w-full items-start justify-between gap-3 border-b border-[var(--border,#e2e8f0)] px-3 py-2 text-left hover:bg-[var(--primary-soft,#dbeafe)] ${activeIndex === index ? 'bg-[var(--primary-soft,#dbeafe)]' : ''}`}>
          <span className="min-w-0"><span className="block truncate text-sm font-semibold text-[var(--text,#0f172a)]">{result.name}</span><span className="block truncate text-xs font-normal text-[var(--muted,#64748b)]">{result.address || 'Address unavailable'}</span></span>
          <Badge variant="secondary">{result.source === 'MTS' ? 'MTS' : 'Google'}</Badge>
        </button>)}
        {loading ? <p className="border-b px-3 py-2 text-xs font-normal text-[var(--muted,#64748b)]">Searching local businesses…</p> : null}
        {!loading && !results.length ? <p className="border-b px-3 py-2 text-xs font-normal text-[var(--muted,#64748b)]">No business matches found.</p> : null}
        <button id={`mailer-business-option-${manualIndex}`} role="option" aria-selected={activeIndex === manualIndex} type="button" onMouseDown={(event) => event.preventDefault()} onClick={chooseManual} className={`block w-full px-3 py-3 text-left text-sm font-semibold text-[var(--primary,#1d4ed8)] hover:bg-[var(--primary-soft,#dbeafe)] ${activeIndex === manualIndex ? 'bg-[var(--primary-soft,#dbeafe)]' : ''}`}>Can&apos;t find your business? → Add it manually</button>
        {googleUnavailable ? <p className="sr-only" role="status">Google suggestions are temporarily unavailable; local search and manual entry remain available.</p> : null}
      </div> : null}
    </div>
  </label>;
}

function MailerLeadCapture({ data, selectedCategoryId, selectedProductId }) {
  const availableCategories = data.categories || data.campaign?.categories || [];
  const categoryGroups = useMemo(() => (
    availableCategories.filter((parent) => Array.isArray(parent.children) && parent.children.length > 0)
  ), [availableCategories]);
  const [form, setForm] = useState({
    businessName: '',
    businessSource: '',
    listingId: null,
    googlePlaceId: null,
    sessionToken: null,
    contactName: '',
    email: '',
    phone: '',
    website: '',
    categoryId: resolveMailerSubcategoryId(categoryGroups, selectedCategoryId),
    adProductId: selectedProductId || '',
    message: '',
  });
  const [status, setStatus] = useState({ state: 'idle', message: '' });

  useEffect(() => {
    setForm((current) => ({ ...current, categoryId: resolveMailerSubcategoryId(categoryGroups, selectedCategoryId) || current.categoryId, adProductId: selectedProductId || current.adProductId }));
  }, [categoryGroups, selectedCategoryId, selectedProductId]);

  const updateField = (field) => (event) => setForm((current) => ({ ...current, [field]: event.target.value }));

  async function handleSubmit(event) {
    event.preventDefault();
    if (!form.businessSource) {
      setStatus({ state: 'error', message: 'Select a business from the search results or choose “Add it manually”.' });
      return;
    }
    if (!form.categoryId) {
      setStatus({ state: 'error', message: 'Choose a business subcategory from the search results.' });
      return;
    }
    setStatus({ state: 'submitting', message: '' });
    trackVisitorEvent({ eventType: 'mailer_lead_started', metadata: { campaignId: data.campaign?.id || null } });
    try {
      const response = await submitSharedMailerLead({
        ...form,
        categoryId: form.categoryId || selectedCategoryId || 'interest-list',
        referrer: document.referrer || '',
        utmSource: new URLSearchParams(window.location.search).get('utm_source') || '',
        utmMedium: new URLSearchParams(window.location.search).get('utm_medium') || '',
        utmCampaign: new URLSearchParams(window.location.search).get('utm_campaign') || '',
      });
      setStatus({ state: 'success', message: response?.message || 'Thanks — your request has been sent.' });
    } catch (error) {
      setStatus({ state: 'error', message: error?.message || 'Unable to submit your request right now.' });
      trackVisitorEvent({ eventType: 'mailer_lead_failed', metadata: { campaignId: data.campaign?.id || null } });
    }
  }

  return (
    <section id="reserve" className="scroll-mt-24 bg-[var(--background,#f8fafc)] py-14">
      <div className="mx-auto grid max-w-7xl gap-8 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr]">
        <div>
          <p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">Reserve</p>
          <h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">Ready to Reach Local Households?</h2>
          <p className="mt-4 text-base leading-7 text-[var(--muted,#64748b)]">
            Reserve your business category for the next {data.tenant.marketName} shared mailer. Availability is confirmed before any reservation becomes final.
          </p>
          {data.campaign?.availableSpotCount ? <p className="mt-4 font-semibold text-[var(--text,#0f172a)]">{data.campaign.availableSpotCount} spaces currently available.</p> : null}
        </div>
        <Card className="border border-[var(--border,#e2e8f0)]">
          <Card.Body>
            {status.state === 'success' ? (
              <div className="rounded-[8px] bg-green-50 p-5 text-green-900">
                <CheckCircle2 size={24} />
                <InlineFeedback variant="status" className="mt-3 font-bold">{status.message}</InlineFeedback>
              </div>
            ) : (
              <form className="grid gap-4" onSubmit={handleSubmit}>
                {status.state === 'error' ? <InlineFeedback variant="status" className="rounded-[8px] bg-red-50 p-3 text-sm text-red-700">{status.message}</InlineFeedback> : null}
                <BusinessAutocomplete value={form.businessName} selection={form.businessSource ? form : null}
                  onChange={(businessName) => setForm((current) => current.businessSource === 'MANUAL'
                    ? ({ ...current, businessName })
                    : ({ ...current, businessName, businessSource: '', listingId: null, googlePlaceId: null, sessionToken: null }))}
                  onSelect={(result) => setForm((current) => result ? ({ ...current, businessName: result.name || current.businessName, businessSource: result.source, listingId: result.listingId || null, googlePlaceId: result.googlePlaceId || null, sessionToken: result.sessionToken || null }) : ({ ...current, businessSource: '', listingId: null, googlePlaceId: null, sessionToken: null }))} />
                <GroupedCategoryCombobox groups={categoryGroups} value={form.categoryId} onChange={(categoryId) => setForm((current) => ({ ...current, categoryId }))} />
                <Input label="Contact" value={form.contactName} onChange={updateField('contactName')} required />
                <div className="grid gap-4 sm:grid-cols-2">
                  <Input label="Email" type="email" value={form.email} onChange={updateField('email')} required />
                  <Input label="Phone" value={form.phone} onChange={updateField('phone')} required={data.config.leadCapture.requirePhone} />
                </div>
                <Button type="submit" size="lg" className={ACCENT_CTA_CLASS} disabled={status.state === 'submitting'}>
                  <Send size={18} />
                  {status.state === 'submitting' ? 'Sending...' : RESERVE_CTA_LABEL}
                </Button>
              </form>
            )}
          </Card.Body>
        </Card>
      </div>
    </section>
  );
}

function SpotlightDealPromo({ campaign }) {
  const deal = (campaign?.adProducts || []).find((product) => product.adSizeSlug === 'stack');
  if (!deal) return null;
  const price = formatCurrency(deal.price);
  return <section className="bg-[var(--surface,#fff)] px-4 py-12 sm:px-6"><div className="mx-auto flex max-w-7xl flex-col gap-6 rounded-3xl border border-[var(--spotlight,#f59e0b)]/40 bg-[var(--spotlight-soft,#fef3c7)] p-7 sm:p-10 lg:flex-row lg:items-center lg:justify-between"><div><p className="text-sm font-bold uppercase tracking-[0.16em] text-[var(--primary,#1d4ed8)]">A low-cost way to get started</p><h2 className="mt-3 text-3xl font-bold text-[var(--text,#0f172a)]">Explore the {price ? `${price} ` : ''}Spotlight Deal</h2><p className="mt-3 max-w-3xl leading-7 text-[var(--muted,#64748b)]">Share one compelling local offer in a compact mailer placement designed to give residents a clear reason to act.</p></div><Link to="/advertise/mailer/spotlight-deal" onClick={() => trackVisitorEvent({ eventType: 'spotlight_deal_promo_clicked', metadata: { adProductId: deal.id, campaignId: campaign?.id || null } })} className={`${ACCENT_CTA_CLASS} inline-flex min-h-11 w-full shrink-0 items-center justify-center gap-2 rounded-lg px-5 py-3 text-sm font-bold outline-none transition focus-visible:ring-2 focus-visible:ring-offset-2 sm:w-auto`}>Learn About the Spotlight Deal<ArrowRight size={18} aria-hidden="true" /></Link></div></section>;
}

export default function SharedMailerLandingPage({ availabilityOnly = false }) {
  const { tenant, spotlightName, tenantLogoConfig, tenantLogoImageUrl } = usePublicTenant();
  const initial = useInitialPublicData("mailer");
  const [data, setData] = useState(initial || null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(!initial);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');

  useEffect(() => {
    let mounted = true;
    getSharedMailerLandingPage()
      .then((payload) => {
        if (!mounted) return;
        setData(payload);
        const requestedProductId = new URLSearchParams(window.location.search).get('adProductId') || '';
        if ((payload?.campaign?.adProducts || []).some((product) => product.id === requestedProductId)) {
          setSelectedProductId(requestedProductId);
        }
        trackVisitorEvent({ eventType: 'mailer_landing_viewed', metadata: { campaignId: payload?.campaign?.id || null } });
      })
      .catch((loadError) => {
        if (mounted) setError(loadError?.message || 'Unable to load this page.');
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);
  usePublicMetadata(() => { if (!tenant || !data || availabilityOnly) return; const path = '/advertise/mailer'; const metadata = buildPublicPageMetadata({ tenant, pathname: path, canonicalPath: path, siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title: data.seo?.title, description: data.seo?.description, imageUrl: data.heroImageUrl, imageAlt: data.heroImageAlt, robots: data.seo?.noindex ? 'noindex,follow' : undefined }); metadata.jsonLd = [buildWebPageSchema({ url: metadata.canonicalUrl, name: metadata.title, description: metadata.description }), buildBreadcrumbSchema({ url: metadata.canonicalUrl, items: [{ name: 'Home', url: tenant.canonicalOrigin }, { name: 'Shared Mail Advertising', url: metadata.canonicalUrl }] })]; return (metadata); }, [availabilityOnly, data, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);
  usePublicMetadata(() => { if (!tenant || loading || data || !error) return; return (buildPublicPageMetadata({ tenant, pathname: availabilityOnly ? '/mailer/availability' : '/advertise/mailer', siteName: spotlightName, cityName: tenantLogoConfig?.cityName, logoUrl: tenantLogoImageUrl, title: `Shared mail advertising unavailable | ${spotlightName}`, description: 'Shared mail advertising information is not currently available.', robots: 'noindex,follow', jsonLd: [] })); }, [availabilityOnly, data, error, loading, spotlightName, tenant, tenantLogoConfig?.cityName, tenantLogoImageUrl]);

  const handleCategorySelect = (categoryId) => {
    setSelectedCategoryId(categoryId);
    trackVisitorEvent({ eventType: 'mailer_category_selected', metadata: { categoryId, campaignId: data?.campaign?.id || null } });
    scrollToSection('reserve');
  };

  const handleProductSelect = (productId) => {
    setSelectedProductId(productId);
    trackVisitorEvent({ eventType: 'mailer_ad_product_selected', metadata: { adProductId: productId, campaignId: data?.campaign?.id || null } });
    scrollToSection('reserve');
  };

  if (loading) {
    return <div className="min-h-screen bg-[var(--background,#f8fafc)] px-4 py-20 text-center text-[var(--muted,#64748b)]">Loading advertising options...</div>;
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-[var(--background,#f8fafc)] px-4 py-20">
        <EmptyState title="Advertising page unavailable" description={error || 'This tenant is not currently accepting public mailer inquiries.'} />
      </div>
    );
  }

  const campaign = data.campaign;
  const sections = data.config.sections || {};

  if (availabilityOnly) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-7xl px-4 pt-8 sm:px-6">
          <Link to="/advertise/mailer" className="text-sm font-bold text-[var(--primary,#1d4ed8)] hover:underline">← Back to mailer overview</Link>
        </div>
        {campaign ? <MailerCategoryAvailability categories={campaign.categories || []} onSelect={handleCategorySelect} showAll /> : (
          <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6"><EmptyState title="No categories available" description="There is no active mailer category inventory right now." /></div>
        )}
        {campaign ? <MailerLeadCapture data={data} selectedCategoryId={selectedCategoryId} selectedProductId={selectedProductId} /> : null}
      </div>
    );
  }

  return (
    <div className="bg-white">
      <MailerSectionNav primaryLabel={RESERVE_CTA_LABEL} />
      <MailerHero data={data} />
      {sections.howItWorks ? <MailerHowItWorks tenant={data.tenant} /> : null}
      {sections.coverage ? <MailerCoverageSection campaign={campaign} coverageMap={data.coverageMap} tenant={data.tenant} /> : null}
      {sections.availability && campaign ? <MailerCategoryAvailability categories={campaign.categories || []} onSelect={handleCategorySelect} /> : null}
      {sections.pricing && campaign ? <MailerPricingSection campaign={campaign} showPricing={data.config.display?.showPricing !== false} onSelect={handleProductSelect} /> : null}
      {sections.examples && data.config.display?.showPreviousMailers !== false ? <MailerExamplesGallery examples={campaign?.examples || data.examples || []} /> : null}
      {sections.platformBenefits ? <MailerPlatformBenefits data={data} /> : null}
      {sections.faq ? <MailerFaq faq={data.config.faq || []} /> : null}
      {sections.finalCta !== false ? (
        <MailerLeadCapture
          data={data}
          selectedCategoryId={selectedCategoryId}
          selectedProductId={selectedProductId}
        />
      ) : null}
      <SpotlightDealPromo campaign={campaign} />
    </div>
  );
}
